// R5.2 — `LegacyStoredView`: THE STORED PREVIOUS-FORMAT REVIEW, READ-ONLY (spec §8.4, R5-OQ-G).
//
// WHY THIS COMPONENT EXISTS AT ALL (R5.2 round-2 finding 1). The cohort states that hand a user back to the
// previous format — the typed `409 legacy_only`, and `409 no_ledger` before it — used to mount `QualityReadView`,
// the legacy surface itself. That does not work for a cohort account and never could: `QualityReadView`'s mount
// calls `GET /api/coverage/cv/:id/quality`, and that route's R5 §0a item 6(b) branch answers a cohort account with
// a typed POINTER (`{rubric_read_cohort, redirect_to, legacy_read_available}`) BEFORE any recompute — deliberately,
// so a cohort account cannot draw LLM-backed legacy reads for free. `QualityReadView` does not know that shape and
// treats it as a failed load. So the honest handoff rendered an error, and the review the applicant had actually
// paid for sat unread in `cvs.quality_read_output`.
//
// THIS COMPONENT CONSUMES THE ONE ENDPOINT BUILT FOR THIS: `GET /api/rubric-read/cv/:id/legacy`, which NEVER
// computes and NEVER writes, is flag-EXEMPT, and answers in typed states — `200 {legacy, read}`, `404
// no_legacy_read`, `409 legacy_read_corrupt`, `404 cv_not_found`. Each of those gets its own registered string
// here; none of them is guessed from another.
//
// WHY A MINIMAL PRESENTATION RATHER THAN `QualityReadView`'s OWN COMPONENTS. Nothing in `QualityReadView` is
// importable for this job. Its section/entry/bullet renderers are module-private, and they are private for a
// reason: `SectionReview` and `BulletReview` carry the improve-editor, the `PATCH /api/bullets/:id` writer, the
// paywall handoff and the per-reader dismissal store — write affordances, in a slice whose whole contract is
// read-only. Exporting them would mean editing `QualityReadView`, which this change may not do, and mounting them
// would mean shipping edit paths against a payload whose bullet ids may since have been archived by the
// atomic-bullet migration. So this renders the stored payload faithfully and inertly instead: every field the
// legacy read carries that the legacy surface displayed as PROSE is displayed, in the payload's own order, with
// no affordance of any kind. The styles are `rubric/styles.ts` — which is CVCraft's existing visual system
// verbatim, the same `var(--token)` set `QualityReadView`'s own `S` object is built from.
//
// THE PAYLOAD IS GRANDFATHERED, AND THAT GOVERNS HOW IT IS READ AND RENDERED:
//   * UNSCANNED. §8.4's ratified exception — legacy payloads predate the banned-output registry and Appendix A
//     entirely and are served verbatim. So this file renders their text as plain React text nodes and never routes
//     it through `Copy`/`TemplateText`, which would scan it against a registry it was never written under. Every
//     string this component authors ITSELF is a registered `CS-*` id, as usual.
//   * UNTRUSTED. It is arbitrary JSON written by some earlier deploy, so nothing here indexes into it on faith:
//     each field is read through a narrow reader that returns nothing when the shape is not what it expects. A
//     legacy read missing `sections` renders its lead and stops; it does not throw.
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { api } from '../lib/api';
import { Copy, LayerHeading, layerHeadingId } from './renderContent';
import { BODY_STYLE, CARD_STYLE, MUTED_STYLE, NOTICE_STYLE, SECTION_HEADING_STYLE, SECTION_STYLE } from './styles';

/** One state per typed answer of `GET .../legacy`, and a catch-all only for transport. */
type LegacyState =
  | { kind: 'loading' }
  | { kind: 'read'; read: unknown }
  /** The typed `404 no_legacy_read` — the ONE answer that means "none exists". */
  | { kind: 'absent' }
  /** The typed `409 legacy_read_corrupt` — one IS on file and will not parse. Not an absence (R5-OQ-J). */
  | { kind: 'corrupt' }
  | { kind: 'error' };

interface LegacyBody {
  legacy?: boolean;
  read?: unknown;
  error?: string;
}

export interface LegacyStoredViewProps {
  cvId: string;
  /**
   * Navigation back to the evaluate flow, rendered on the ABSENT state only.
   *
   * The affordance's label is `CS-EMPTY-NO-READ` itself — "No review yet. Import your CV to generate one." — which
   * is the registered sentence for this state and already names the action. Making that sentence the control is
   * what keeps T-COPY-1 true: this surface registers no new string and invents no CTA wording, and the applicant
   * still gets a keyboard-operable, announced way out instead of a dead end.
   */
  onImportCv?: () => void;
  /** Bumped by the parent to force a re-read, exactly like `RubricReadView`'s. */
  reloadKey?: number;
}

export function LegacyStoredView({ cvId, onImportCv, reloadKey = 0 }: LegacyStoredViewProps) {
  const [state, setState] = useState<LegacyState>({ kind: 'loading' });
  // THE SAME GENERATION GUARD `RubricReadView` runs (round-2 advisory). A component reused for a second CV must
  // not have the FIRST CV's stored review land in it because that request resolved later.
  const gen = useRef(0);

  useEffect(() => {
    const mine = ++gen.current;
    setState({ kind: 'loading' });
    void (async () => {
      let res: Awaited<ReturnType<typeof api<LegacyBody>>>;
      try {
        res = await api<LegacyBody>(`/api/rubric-read/cv/${cvId}/legacy`);
      } catch {
        if (gen.current === mine) setState({ kind: 'error' });
        return;
      }
      if (gen.current !== mine) return;
      if (res.ok && res.data && 'read' in res.data) return setState({ kind: 'read', read: res.data.read });
      if (res.status === 404 && res.data?.error === 'no_legacy_read') return setState({ kind: 'absent' });
      if (res.status === 409 && res.data?.error === 'legacy_read_corrupt') return setState({ kind: 'corrupt' });
      setState({ kind: 'error' });
    })();
    return () => {
      // Invalidate on unmount as well as on re-run: a late resolution must not write into a component that is gone.
      gen.current += 1;
    };
  }, [cvId, reloadKey]);

  if (state.kind === 'loading') {
    // ANNOUNCED **AND SAID** (R5.3 round 2, finding 8). Round 1 rendered an empty `role="status"`, on the
    // reasoning that Appendix A registered no loading string; an empty live region announces nothing, so the
    // wait was silent for exactly the users the live region exists for. `CS-LEGACY-LOADING` is registered
    // (A.5-P) and names WHICH thing is loading, since this surface can be mounted beside the rubric one.
    return (
      <div role="status" aria-busy="true" data-testid="legacy-loading">
        <Copy id="CS-LEGACY-LOADING" />
      </div>
    );
  }
  if (state.kind === 'absent') {
    return (
      <div data-testid="legacy-absent">
        {onImportCv ? (
          <button type="button" className="btn btn-secondary" onClick={onImportCv} data-testid="legacy-import-cta">
            <Copy id="CS-EMPTY-NO-READ" />
          </button>
        ) : (
          <p style={NOTICE_STYLE} data-testid="rubric-empty">
            <Copy id="CS-EMPTY-NO-READ" />
          </p>
        )}
      </div>
    );
  }
  if (state.kind === 'corrupt' || state.kind === 'error') {
    // A legacy read that is ON FILE and unreadable is NOT an absence, and this component may not say it is
    // (R5-OQ-J). Both cases are honest unavailability about our ability to answer.
    return (
      <p style={NOTICE_STYLE} role="alert" data-testid="legacy-unavailable">
        <Copy id="CS-ERR-READ-UNAVAILABLE" />
      </p>
    );
  }
  return <LegacyBody_ read={state.read} />;
}

/* ============================================================================================================== *
 * THE PRESENTATION — inert, faithful, and defensive about a payload no schema governs.
 * ============================================================================================================== */

const isObj = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v);
/** A displayable string: present, a string, and not empty after trimming. Anything else renders nothing. */
const text = (v: unknown): string | null => (typeof v === 'string' && v.trim().length > 0 ? v : null);
const list = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

/** The whole stored read. `read` is `unknown` on purpose — it is whatever an older deploy wrote. */
function LegacyBody_({ read }: { read: unknown }) {
  const r = isObj(read) ? read : {};
  const lead = text(r.lead);
  const overall = text(r.overall);
  const sections = list(r.sections);
  return (
    <div data-testid="legacy-stored-read">
      {/* The banner is THIS surface's own registered copy, and it says what the payload below is. */}
      <p style={NOTICE_STYLE} data-testid="legacy-banner">
        <Copy id="CS-LEGACY-BANNER" />
      </p>
      {lead && <Prose testid="legacy-lead" style={BODY_STYLE}>{lead}</Prose>}
      {overall && <Prose testid="legacy-overall" style={BODY_STYLE}>{overall}</Prose>}
      {sections.map((s, i) => (
        <LegacySection key={sectionKey(s, i)} section={s} />
      ))}
    </div>
  );
}

/** A stable key that does not depend on the payload being well-formed. */
function sectionKey(s: unknown, i: number): string {
  return (isObj(s) && text(s.section)) || `section-${i}`;
}

function LegacySection({ section }: { section: unknown }) {
  const s = isObj(section) ? section : {};
  const name = text(s.section);
  const recommendation = text(s.recommendation);
  const items = list(s.items);
  const strong = list(s.strongEntries);
  if (!name && items.length === 0 && strong.length === 0 && !recommendation) return null;
  return (
    // NAMED BY A REGISTERED HEADING (accessibility audit A11Y-1, round 3), not by the section name below it.
    //
    // The `<h2>` that follows carries a name out of the STORED LEGACY PAYLOAD — a grandfathered, unscanned
    // string (§0a item 6's legacy exception) — so it cannot serve as this region's registered name without
    // making a T-COPY-1 claim about content that predates Appendix A entirely. The registered heading names the
    // region; the payload's own section name stays exactly where it was, as content.
    <section
      aria-labelledby={layerHeadingId('CS-SECTION-LEGACY')}
      style={SECTION_STYLE}
      data-testid="legacy-section"
    >
      <LayerHeading id="CS-SECTION-LEGACY" />
      {name && (
        <h3 style={{ ...SECTION_HEADING_STYLE, fontSize: 'var(--fs-h3)' }}>
          <span>{name}</span>
        </h3>
      )}
      {recommendation && <Prose testid="legacy-recommendation" style={BODY_STYLE}>{recommendation}</Prose>}
      {items.map((it, i) => (
        <LegacyItem key={entryKey(it, i)} item={it} />
      ))}
      {strong.map((it, i) => (
        <LegacyStrongEntry key={entryKey(it, i, 'strong')} entry={it} />
      ))}
    </section>
  );
}

function entryKey(it: unknown, i: number, prefix = 'item'): string {
  return (isObj(it) && text(it.entry_id)) || `${prefix}-${i}`;
}

/** One reviewed entry: its own title/organization, then each reviewed bullet with its diagnosis and question. */
function LegacyItem({ item }: { item: unknown }) {
  const it = isObj(item) ? item : {};
  const title = text(it.entry_title);
  const org = text(it.entry_organization);
  const bullets = list(it.bullets);
  if (!title && bullets.length === 0) return null;
  return (
    <div style={CARD_STYLE} data-testid="legacy-entry">
      {title && (
        <Prose testid="legacy-entry-title" style={{ ...BODY_STYLE, fontWeight: 'var(--fw-semibold)' }}>
          {org ? `${title} — ${org}` : title}
        </Prose>
      )}
      {bullets.map((b, i) => (
        <LegacyBullet key={bulletKey(b, i)} bullet={b} />
      ))}
    </div>
  );
}

function bulletKey(b: unknown, i: number): string {
  return (isObj(b) && text(b.bullet_id)) || `bullet-${i}`;
}

/**
 * One reviewed bullet: the applicant's own line, then the stored diagnosis and question.
 *
 * READ-ONLY, ABSOLUTELY. The legacy surface offered an "Improve" editor here that writes through
 * `PATCH /api/bullets/:id`. R5.2 issues no write of any kind, and a stored legacy payload's bullet ids may have
 * been archived by the atomic-bullet migration since it was written — so the words are shown and nothing is
 * offered to do with them.
 */
function LegacyBullet({ bullet }: { bullet: unknown }) {
  const b = isObj(bullet) ? bullet : {};
  const body = text(b.bullet_text);
  const diagnosis = text(b.diagnosis);
  const question = text(b.question);
  if (!body && !diagnosis && !question) return null;
  return (
    <div data-testid="legacy-bullet">
      {body && <Prose testid="legacy-bullet-text" style={BODY_STYLE}>{body}</Prose>}
      {diagnosis && <Prose testid="legacy-diagnosis" style={MUTED_STYLE}>{diagnosis}</Prose>}
      {question && <Prose testid="legacy-question" style={MUTED_STYLE}>{question}</Prose>}
    </div>
  );
}

/** An entry the legacy read called strong: the applicant's own title and bullets, with no critique attached. */
function LegacyStrongEntry({ entry }: { entry: unknown }) {
  const e = isObj(entry) ? entry : {};
  const title = text(e.entry_title);
  const bullets = list(e.bullets);
  if (!title && bullets.length === 0) return null;
  return (
    <div style={CARD_STYLE} data-testid="legacy-strong-entry">
      {title && (
        <Prose testid="legacy-entry-title" style={{ ...BODY_STYLE, fontWeight: 'var(--fw-semibold)' }}>
          {title}
        </Prose>
      )}
      {bullets.map((b, i) => {
        const body = isObj(b) ? text(b.bullet_text) : null;
        return body ? (
          <Prose key={bulletKey(b, i)} testid="legacy-bullet-text" style={BODY_STYLE}>
            {body}
          </Prose>
        ) : null;
      })}
    </div>
  );
}

/**
 * A grandfathered string, rendered as a TEXT NODE and nothing else.
 *
 * `data-copy-src="legacy_stored"` marks it for what it is, so the T-COPY-1 DOM sweep can tell a §8.4-exempt
 * payload string apart from a registered one instead of having to infer it — and so a legacy string can never be
 * mistaken for evidence that some registry id resolved. Rule 2 of §1.5 still holds: the applicant's own words go
 * in the text node, never in the attribute.
 */
function Prose({ children, style, testid }: { children: ReactNode; style: CSSProperties; testid: string }) {
  return (
    <p style={style} data-copy-src="legacy_stored" data-testid={testid}>
      {children}
    </p>
  );
}
